/**
 * Semantic Defaults Service
 * 
 * Defines and enforces canonical vocabulary for prioritization sessions.
 * Makes C2 (Semantic defaults govern sessions) true.
 * 
 * This ensures consistent terminology across:
 * - Session configurations
 * - Tool naming and parameters
 * - Status labels and lifecycle states
 * - Criteria and scoring terminology
 */

// ============================================================================
// Canonical Vocabulary
// ============================================================================

/**
 * Standard lifecycle states for prioritizable entities
 */
export const LIFECYCLE_STATES = {
  IDEA: 'idea',           // Raw intake, unstructured
  ITEM: 'item',           // Shaped, has attributes
  PRIORITY: 'priority',   // Ranked, on a focus list
  ARCHIVED: 'archived',   // No longer active
} as const;

export type LifecycleState = typeof LIFECYCLE_STATES[keyof typeof LIFECYCLE_STATES];

/**
 * Standard status values for focus lists
 */
export const FOCUS_LIST_STATUS = {
  DRAFT: 'draft',
  PENDING_APPROVAL: 'pending_approval',
  PUBLISHED: 'published',
  ARCHIVED: 'archived',
} as const;

export type FocusListStatus = typeof FOCUS_LIST_STATUS[keyof typeof FOCUS_LIST_STATUS];

/**
 * Standard DEGAP phases
 */
export const DEGAP_PHASES = {
  DECIDE: 'DECIDE',
  ENGAGE: 'ENGAGE',
  GATHER: 'GATHER',
  ARRANGE: 'ARRANGE',
  PRIORITIZE: 'PRIORITIZE',
} as const;

export type DegapPhase = typeof DEGAP_PHASES[keyof typeof DEGAP_PHASES];

/**
 * Standard scoring scales
 */
export const SCORING_SCALES = {
  BINARY: { min: 0, max: 1, name: 'Binary (0-1)' },
  THREE_POINT: { min: 1, max: 3, name: 'Three-Point (1-3)' },
  FIVE_POINT: { min: 1, max: 5, name: 'Five-Point (1-5)' },
  SEVEN_POINT: { min: 1, max: 7, name: 'Seven-Point (1-7)' },
  TEN_POINT: { min: 1, max: 10, name: 'Ten-Point (1-10)' },
  FIBONACCI: { min: 1, max: 21, name: 'Fibonacci (1-21)', values: [1, 2, 3, 5, 8, 13, 21] },
  T_SHIRT: { min: 1, max: 5, name: 'T-Shirt (XS-XL)', labels: ['XS', 'S', 'M', 'L', 'XL'] },
} as const;

export type ScoringScaleKey = keyof typeof SCORING_SCALES;

/**
 * Standard criteria categories
 */
export const CRITERIA_CATEGORIES = {
  VALUE: 'value',           // Business/user value
  COST: 'cost',             // Effort, resources
  RISK: 'risk',             // Uncertainty, dependencies
  STRATEGIC: 'strategic',   // Alignment with goals
  URGENCY: 'urgency',       // Time sensitivity
} as const;

export type CriteriaCategory = typeof CRITERIA_CATEGORIES[keyof typeof CRITERIA_CATEGORIES];

/**
 * Standard tool categories
 */
export const TOOL_CATEGORIES = {
  SCORING: 'scoring',           // Numeric scoring methods
  RANKING: 'ranking',           // Comparative ranking
  CATEGORIZATION: 'categorization', // Bucketing/grouping
  VOTING: 'voting',             // Collaborative selection
  MATRIX: 'matrix',             // 2D positioning
} as const;

export type ToolCategory = typeof TOOL_CATEGORIES[keyof typeof TOOL_CATEGORIES];

/**
 * Canonical tool names (for consistent reference)
 */
export const CANONICAL_TOOLS = {
  RICE: 'rice',
  WSJF: 'wsjf',
  MOSCOW: 'moscow',
  EISENHOWER: 'eisenhower',
  IMPACT_EFFORT: 'impact-effort',
  DOT_VOTING: 'dot-voting',
  STACK_RANKING: 'stack-ranking',
  PAIRWISE: 'pairwise',
  WEIGHTED_SCORING: 'weighted-scoring',
  COST_OF_DELAY: 'cost-of-delay',
  PARETO: 'pareto',
  KANO: 'kano',
} as const;

export type CanonicalTool = typeof CANONICAL_TOOLS[keyof typeof CANONICAL_TOOLS];

// ============================================================================
// Session Defaults
// ============================================================================

/**
 * Default configuration for new sessions
 */
export interface SessionDefaults {
  defaultScoringScale: ScoringScaleKey;
  defaultCriteriaCategories: CriteriaCategory[];
  minItemsForSession: number;
  maxItemsForSession: number;
  requireRationaleForRanking: boolean;
  requireRationaleForCutLine: boolean;
  allowAnonymousVoting: boolean;
  defaultVotingBudget: number;
}

export const DEFAULT_SESSION_CONFIG: SessionDefaults = {
  defaultScoringScale: 'FIVE_POINT',
  defaultCriteriaCategories: ['value', 'cost', 'risk'],
  minItemsForSession: 3,
  maxItemsForSession: 50,
  requireRationaleForRanking: false,
  requireRationaleForCutLine: true,
  allowAnonymousVoting: false,
  defaultVotingBudget: 10,
};

/**
 * Workspace-level overrides for session defaults
 */
export interface WorkspaceSemanticConfig {
  workspaceId: string;
  sessionDefaults: Partial<SessionDefaults>;
  customVocabulary?: Record<string, string>; // e.g., { "priority": "OKR", "item": "initiative" }
  enabledTools: CanonicalTool[];
  disabledFeatures: string[];
  updatedAt: string;
}

// ============================================================================
// Validation & Enforcement
// ============================================================================

/**
 * Validate that a lifecycle state is canonical
 */
export function isValidLifecycleState(state: string): state is LifecycleState {
  return Object.values(LIFECYCLE_STATES).includes(state as LifecycleState);
}

/**
 * Validate that a DEGAP phase is canonical
 */
export function isValidDegapPhase(phase: string): phase is DegapPhase {
  return Object.values(DEGAP_PHASES).includes(phase as DegapPhase);
}

/**
 * Validate that a tool name is canonical
 */
export function isValidToolName(tool: string): tool is CanonicalTool {
  return Object.values(CANONICAL_TOOLS).includes(tool as CanonicalTool);
}

/**
 * Normalize a tool name to canonical form
 */
export function normalizeToolName(input: string): CanonicalTool | null {
  const normalized = input.toLowerCase().replace(/[_\s]/g, '-');
  
  // Direct match
  if (isValidToolName(normalized)) {
    return normalized;
  }
  
  // Common aliases
  const aliases: Record<string, CanonicalTool> = {
    'reach-impact-confidence-effort': 'rice',
    'weighted-shortest-job-first': 'wsjf',
    'must-should-could-wont': 'moscow',
    'urgent-important': 'eisenhower',
    'effort-impact': 'impact-effort',
    'voting': 'dot-voting',
    'ranking': 'stack-ranking',
    'pairwise-comparison': 'pairwise',
    'weighted': 'weighted-scoring',
    'cod': 'cost-of-delay',
    '80-20': 'pareto',
  };
  
  return aliases[normalized] || null;
}

/**
 * Get merged session defaults (workspace overrides + global defaults)
 */
export function getMergedSessionDefaults(
  workspaceConfig?: Partial<WorkspaceSemanticConfig>
): SessionDefaults {
  if (!workspaceConfig?.sessionDefaults) {
    return DEFAULT_SESSION_CONFIG;
  }
  
  return {
    ...DEFAULT_SESSION_CONFIG,
    ...workspaceConfig.sessionDefaults,
  };
}

/**
 * Validate session configuration against semantic defaults
 */
export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export function validateSessionConfig(config: {
  itemCount?: number;
  toolName?: string;
  scoringScale?: string;
}, defaults: SessionDefaults = DEFAULT_SESSION_CONFIG): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  // Validate item count
  if (config.itemCount !== undefined) {
    if (config.itemCount < defaults.minItemsForSession) {
      errors.push(`Session requires at least ${defaults.minItemsForSession} items (got ${config.itemCount})`);
    }
    if (config.itemCount > defaults.maxItemsForSession) {
      warnings.push(`Session has ${config.itemCount} items, which exceeds recommended max of ${defaults.maxItemsForSession}`);
    }
  }
  
  // Validate tool name
  if (config.toolName && !isValidToolName(config.toolName)) {
    const normalized = normalizeToolName(config.toolName);
    if (normalized) {
      warnings.push(`Tool name "${config.toolName}" normalized to "${normalized}"`);
    } else {
      errors.push(`Unknown tool: "${config.toolName}". Valid tools: ${Object.values(CANONICAL_TOOLS).join(', ')}`);
    }
  }
  
  // Validate scoring scale
  if (config.scoringScale && !(config.scoringScale in SCORING_SCALES)) {
    errors.push(`Unknown scoring scale: "${config.scoringScale}". Valid scales: ${Object.keys(SCORING_SCALES).join(', ')}`);
  }
  
  
  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

// ============================================================================
// Display Helpers
// ============================================================================

/**
 * Get human-readable label for a lifecycle state
 */
export function getLifecycleStateLabel(state: LifecycleState): string {
  const labels: Record<LifecycleState, string> = {
    idea: 'Idea',
    item: 'Item',
    priority: 'Priority',
    archived: 'Archived',
  };
  return labels[state] || state;
}

/**
 * Get human-readable label for a DEGAP phase
 */
export function getDegapPhaseLabel(phase: DegapPhase): string {
  const labels: Record<DegapPhase, string> = {
    DECIDE: 'Decide',
    ENGAGE: 'Engage',
    GATHER: 'Gather',
    ARRANGE: 'Arrange',
    PRIORITIZE: 'Prioritize',
  };
  return labels[phase] || phase;
}

/**
 * Get human-readable description for a DEGAP phase
 */
export function getDegapPhaseDescription(phase: DegapPhase): string {
  const descriptions: Record<DegapPhase, string> = {
    DECIDE: 'Define scope, goals, and success criteria for prioritization',
    ENGAGE: 'Identify stakeholders and establish participation rules',
    GATHER: 'Collect and shape items to be prioritized',
    ARRANGE: 'Configure criteria and comparison methods',
    PRIORITIZE: 'Run tools, review results, and commit priorities',
  };
  return descriptions[phase] || '';
}

/**
 * Get tool display info
 */
export function getToolDisplayInfo(tool: CanonicalTool): {
  name: string;
  category: ToolCategory;
  description: string;
} {
  const info: Record<CanonicalTool, { name: string; category: ToolCategory; description: string }> = {
    rice: { name: 'RICE Scoring', category: 'scoring', description: 'Reach, Impact, Confidence, Effort' },
    wsjf: { name: 'WSJF', category: 'scoring', description: 'Weighted Shortest Job First' },
    moscow: { name: 'MoSCoW', category: 'categorization', description: 'Must, Should, Could, Won\'t' },
    eisenhower: { name: 'Eisenhower Matrix', category: 'matrix', description: 'Urgent vs Important' },
    'impact-effort': { name: 'Impact/Effort Matrix', category: 'matrix', description: '2x2 value vs cost' },
    'dot-voting': { name: 'Dot Voting', category: 'voting', description: 'Collaborative budget voting' },
    'stack-ranking': { name: 'Stack Ranking', category: 'ranking', description: 'Force-ranked list' },
    pairwise: { name: 'Pairwise Comparison', category: 'ranking', description: 'Head-to-head comparisons' },
    'weighted-scoring': { name: 'Weighted Scoring', category: 'scoring', description: 'Custom weighted criteria' },
    'cost-of-delay': { name: 'Cost of Delay', category: 'scoring', description: 'Time-value based scoring' },
    pareto: { name: 'Pareto Analysis', category: 'scoring', description: '80/20 contribution analysis' },
    kano: { name: 'Kano Model', category: 'categorization', description: 'Satisfaction vs functionality' },
  };
  return info[tool] || { name: tool, category: 'scoring', description: '' };
}

// ============================================================================
// Workspace Configuration Service
// ============================================================================

/**
 * Get workspace semantic configuration from database
 */
export async function getWorkspaceSemanticConfig(
  workspaceId: string
): Promise<WorkspaceSemanticConfig | null> {
  // This would query a workspace_config table if it existed
  // For now, return null to use defaults
  console.log(`Loading semantic config for workspace: ${workspaceId}`);
  
  // Placeholder - in production this would fetch from database
  return null;
}

/**
 * Save workspace semantic configuration
 */
export async function saveWorkspaceSemanticConfig(
  config: WorkspaceSemanticConfig
): Promise<void> {
  // This would upsert to a workspace_config table
  console.log(`Saving semantic config for workspace: ${config.workspaceId}`, config);
  
  // Placeholder - in production this would persist to database
}

/**
 * Apply custom vocabulary to a string
 */
export function applyCustomVocabulary(
  text: string,
  customVocabulary?: Record<string, string>
): string {
  if (!customVocabulary) return text;
  
  let result = text;
  for (const [canonical, custom] of Object.entries(customVocabulary)) {
    // Case-insensitive replacement
    const regex = new RegExp(canonical, 'gi');
    result = result.replace(regex, custom);
  }
  return result;
}


